import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getProfilesByUser } from "../actions/profile";
import { fetchFile } from "../actions/upload";

const readStoredAuth = () => {
  try {
    return JSON.parse(localStorage.getItem("profile")) || null;
  } catch (error) {
    return null;
  }
};

const getRegisteredUser = (authData) =>
  authData?.result || authData?.user || authData || {};

const getRegisteredName = (registeredUser) =>
  registeredUser?.name ||
  registeredUser?.username ||
  [registeredUser?.firstName, registeredUser?.lastName].filter(Boolean).join(" ");

export const useAccountProfile = ({ fetchProfile = true } = {}) => {
  const dispatch = useDispatch();
  const authData = useSelector((state) => state.auth?.authData);
  const storedAuth = useMemo(readStoredAuth, [authData]);
  const user = authData || storedAuth;
  const registeredUser = getRegisteredUser(user);
  const savedProfile = useSelector((state) => {
    const profile = state.profiles?.profiles;
    return profile && !Array.isArray(profile) ? profile : null;
  });
  const [avatarSrc, setAvatarSrc] = useState("");

  const registeredUserId =
    registeredUser?._id || registeredUser?.id || registeredUser?.googleId || "";
  const registeredEmail = registeredUser?.email || "";
  const registeredName = getRegisteredName(registeredUser) || "";
  const registeredPhone = registeredUser?.phone || registeredUser?.phoneNumber || "";

  useEffect(() => {
    if (fetchProfile && registeredUserId) {
      dispatch(getProfilesByUser({ search: registeredUserId }));
    }
  }, [dispatch, fetchProfile, registeredUserId]);

  const accountProfile = useMemo(
    () => ({
      ...(savedProfile || {}),
      name: savedProfile?.name || registeredName,
      email: registeredEmail || savedProfile?.email || "",
      phoneNumber: savedProfile?.phoneNumber || registeredPhone,
      userId: savedProfile?.userId || registeredUserId,
    }),
    [
      savedProfile,
      registeredEmail,
      registeredName,
      registeredPhone,
      registeredUserId,
    ],
  );

  const displayName = accountProfile.name || registeredEmail || "";
  const displayEmail = accountProfile.email || registeredEmail || "";
  const avatarLetter = (displayName || displayEmail || "U").trim().charAt(0).toUpperCase();
  const imageIdOrUrl = String(accountProfile.logo || "");

  useEffect(() => {
    let isMounted = true;

    const resolveAvatar = async () => {
      if (!imageIdOrUrl) {
        setAvatarSrc("");
        return;
      }

      if (
        imageIdOrUrl.startsWith("data:") ||
        imageIdOrUrl.startsWith("http") ||
        imageIdOrUrl.startsWith("/static/")
      ) {
        setAvatarSrc(imageIdOrUrl);
        return;
      }

      const resolvedFile = await dispatch(fetchFile(imageIdOrUrl));
      if (isMounted) {
        setAvatarSrc(resolvedFile || "");
      }
    };

    resolveAvatar();

    return () => {
      isMounted = false;
    };
  }, [dispatch, imageIdOrUrl]);

  return {
    user,
    registeredUser,
    registeredUserId,
    registeredEmail,
    registeredName,
    registeredPhone,
    savedProfile,
    accountProfile,
    displayName,
    displayEmail,
    avatarLetter,
    avatarSrc,
  };
};

export default useAccountProfile;
